import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { publicDataService } from '../services/publicDataService';
import SEO from '../components/SEO';
import PageTransition from '../components/PageTransition';
import * as FiIcons from 'react-icons/fi';
const { FiMail, FiPhone, FiMapPin, FiSend, FiCheckCircle } = FiIcons;
import SafeIcon from '../common/SafeIcon';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const res = await publicDataService.createContactMessage(formData);
      if (res.status === 'success') {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <PageTransition>
      <SEO title="Contact Us | R. Venue Apartelle" />
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h1 className="font-serif text-4xl md:text-6xl text-brand-olive mb-4">Get In Touch</h1>
              <div className="w-24 h-1 bg-brand-gold mb-8"></div>
              <p className="text-brand-olive/70 text-lg mb-12">
                We'd love to hear from you. Whether you have questions about our rooms, rates, or the Lucban area, our team is here to help.
              </p>

              <div className="space-y-8">
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 bg-brand-cream rounded-full flex items-center justify-center text-brand-olive shrink-0">
                    <SafeIcon icon={FiPhone} className="text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-brand-olive uppercase tracking-widest text-sm mb-1">Call Us</h3>
                    <p className="text-brand-olive/80">+63 917 123 4567</p>
                  </div>
                </div>
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 bg-brand-cream rounded-full flex items-center justify-center text-brand-olive shrink-0">
                    <SafeIcon icon={FiMail} className="text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-brand-olive uppercase tracking-widest text-sm mb-1">Email Us</h3>
                    <p className="text-brand-olive/80">bookings@rvenueapartelle.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 bg-brand-cream rounded-full flex items-center justify-center text-brand-olive shrink-0">
                    <SafeIcon icon={FiMapPin} className="text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-brand-olive uppercase tracking-widest text-sm mb-1">Visit Us</h3>
                    <p className="text-brand-olive/80">Lucban-Tayabas Rd, Lucban, Quezon, Philippines</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-2xl border border-brand-olive/5">
              {status === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <SafeIcon icon={FiCheckCircle} className="text-4xl" />
                  </div>
                  <h2 className="text-2xl font-serif text-brand-olive mb-4">Message Sent!</h2>
                  <p className="text-brand-olive/60 mb-8">Thank you for reaching out. We will get back to you shortly.</p>
                  <button onClick={() => setStatus('idle')} className="text-brand-gold font-bold uppercase tracking-widest text-sm hover:underline">Send another message</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40">Full Name</label>
                      <input 
                        type="text" required value={formData.name}
                        onChange={e => setFormData({...formData, name: e.target.value})}
                        className="w-full bg-brand-cream/30 border border-brand-olive/10 px-4 py-3 outline-none focus:border-brand-gold transition-colors"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40">Email Address</label>
                      <input 
                        type="email" required value={formData.email}
                        onChange={e => setFormData({...formData, email: e.target.value})}
                        className="w-full bg-brand-cream/30 border border-brand-olive/10 px-4 py-3 outline-none focus:border-brand-gold transition-colors"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40">Subject</label>
                    <input 
                      type="text" required value={formData.subject}
                      onChange={e => setFormData({...formData, subject: e.target.value})}
                      className="w-full bg-brand-cream/30 border border-brand-olive/10 px-4 py-3 outline-none focus:border-brand-gold transition-colors"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40">Message</label>
                    <textarea 
                      required rows="5" value={formData.message}
                      onChange={e => setFormData({...formData, message: e.target.value})}
                      className="w-full bg-brand-cream/30 border border-brand-olive/10 px-4 py-3 outline-none focus:border-brand-gold transition-colors resize-none"
                    ></textarea>
                  </div>
                  <button 
                    type="submit" disabled={status === 'submitting'}
                    className="w-full bg-brand-olive text-white font-bold uppercase tracking-[0.2em] py-4 hover:bg-brand-dark transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                  >
                    {status === 'submitting' ? 'Sending...' : (
                      <>
                        <SafeIcon icon={FiSend} /> Send Message
                      </>
                    )}
                  </button>
                  {status === 'error' && <p className="text-red-500 text-xs text-center font-bold">Failed to send message. Please try again.</p>}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageTransition>
  );
};

export default Contact;